import { formatLength, formatRange } from 'common/date-time'
import { namesInProse, verbFor } from '../names-in-prose'
import { coveredByOwnPlaylist, ownPlaylistCoverSentences } from '../own-playlist-cover'
import template from './preflight-report.tpl.html'
import './preflight-report.component.less'

/**
 * Give the stored interval of a casualty. The panel opens on these values.
 */
function storedIntervalOf (casualty) {
  return Object.freeze({
    intervalId: casualty.intervalId,
    sceneCollectionId: casualty.sceneCollectionId,
    role: casualty.role || null,
    start: casualty.start_datetime,
    end: casualty.end_datetime,
    updatedAt: casualty.updatedAt || null
  })
}

/**
 * Write one casualty as sentences for the operator.
 *
 * There are three causes, and each cause has a different repair. The first
 * cause is another playlist on screen for the full span of the interval. A
 * cover by the same playlist is a special case, because the interval then
 * adds nothing to the screen. The second cause is another interval with the
 * same role. Nothing decides between the two. The third cause is an interval
 * with no role. Such an interval loses even an empty stretch.
 *
 * The panel shows "Put on screen" only for an interval with the same role.
 * The report does not get the role of a covering interval, so the advice
 * gives that condition.
 */
function sentencesFor (casualty) {
  const covered = casualty.coveredBy || []
  const tied = casualty.tiedWith || []

  const line = {
    intervalId: casualty.intervalId,
    playlist: casualty.playlist,
    when: formatRange(casualty.start_datetime, casualty.end_datetime),
    interval: storedIntervalOf(casualty)
  }

  if (covered.length > 0 && coveredByOwnPlaylist(casualty.coveredByPlaylistIds, casualty.sceneCollectionId)) {
    return Object.assign(line, ownPlaylistCoverSentences(casualty.playlist))
  }

  if (covered.length > 0) {
    const names = namesInProse(covered)
    const length = formatLength(casualty.start_datetime, casualty.end_datetime)

    line.cause = `${names} ${verbFor(covered, 'covers', 'cover')} it for its whole duration.` +
      (length ? ` It loses ${length}.` : '')
    line.remedy = `Shorten or move ${names}. If ${names} ${verbFor(covered, 'has', 'have')} the same role, ` +
      `open ${casualty.playlist}, click Put on screen and save.`
    return line
  }

  if (tied.length > 0) {
    line.cause = `It has the same role as ${namesInProse(tied)} here, so no playlist is chosen.`
    line.remedy = `Open ${casualty.playlist}, click Put on screen and save. ` +
      'Or change the times so they do not overlap.'
    return line
  }

  line.cause = 'The interval has no role, so it is never on screen.'
  line.remedy = 'Open the interval, select a role and save.'
  return line
}

/**
 * Write one stretch where the schedule names no winner as sentences.
 *
 * Two playlists hold the highest priority there, and the operator knows that
 * as the same role. The player still shows one of them, because a screen
 * cannot go dark on those grounds, so the operator sees a picture that nobody
 * chose. That is the thing to repair.
 *
 * No interval is a casualty here. Both still reach the screen for the rest of
 * their run, which is why the report needs its own row for this.
 */
function stretchSentencesFor (stretch) {
  const playlists = stretch.playlists || []

  const when = formatRange(stretch.start, stretch.end)

  return {
    when,
    headline: `No playlist is chosen for ${when}.`,
    cause: `${namesInProse(playlists)} ${verbFor(playlists, 'has', 'have')} the same role here, for ${formatLength(stretch.start, stretch.end)}.`,
    remedy: 'Open one of them, click Put on screen and save. Or change the times so they do not overlap.'
  }
}

/**
 * This component shows the preflight check. It lists every interval on the scene
 * that never reaches the screen. Each row gives the cause and the repair.
 *
 * The server does the arithmetic with the same module that the on-screen strip
 * uses. The card and the strip therefore agree. This component only makes the
 * sentences.
 *
 * The card is the shared dialog element, so the browser owns the top layer, the
 * dimmed page, the focus trap and the Escape key. Every dialog of the app reads
 * the same element, and this page keeps no overlay of its own.
 */
class PreflightReportController {
  constructor () {
    this.groups = []
  }

  $onChanges (changes) {
    if (!changes.report) {
      return
    }

    this.groups = ((this.report && this.report.scenes) || []).map(scene => ({
      sceneName: scene.sceneName,
      casualties: (scene.casualties || []).map(sentencesFor),
      undefinedStretches: (scene.undefinedStretches || []).map(stretchSentencesFor)
    }))
  }

  get isLoading () {
    return Boolean(this.report && this.report.loading)
  }

  get errorText () {
    return (this.report && this.report.error) || null
  }

  /**
   * True only when a report arrived with nothing in it. A scene reaches the
   * result when it loses an interval or when it holds a stretch with no defined
   * winner, so an empty result means both are absent. A missing report is not a
   * clear result.
   */
  get isClear () {
    return Boolean(this.report && this.report.scenes) && this.groups.length === 0
  }

  /** The caller checks one scene only, so the heading names that scene. */
  get heading () {
    return this.sceneName ? `Preflight: ${this.sceneName}` : 'Preflight'
  }

  /**
   * Outline the interval that a row points at. The dimmed page is see-through,
   * so the operator sees the bar behind the card.
   */
  highlight (intervalId) {
    this.onHighlight({ $intervalId: intervalId })
  }

  clearHighlight () {
    this.onHighlight({ $intervalId: null })
  }

  /** Open the interval of a row in the panel. The page closes the card. */
  open (casualty) {
    this.onOpen({ $interval: casualty.interval })
  }

  /** The row is also a button for the keyboard. Enter and Space open it. */
  openOnKey (event, casualty) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    this.open(casualty)
  }

  close () {
    this.onClose()
  }
}

export const PreflightReportComponent = {
  bindings: {
    report: '<',
    sceneName: '<',
    onClose: '&',
    onHighlight: '&',
    onOpen: '&'
  },
  controller: PreflightReportController,
  template
}
